import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, describeError } from "../../lib/errors";
import { useProject } from "../projects/useProject";
import { DEFAULT_CONFIG, normalizeConfig } from "../projects/config/defaults";
import type { ProjectConfig } from "../projects/config/types";
import { isEqual } from "../projects/config/utils";
import { getBotSettings, patchBotSettings } from "./api";
import { DEFAULT_BOTS_CONFIG, normalizeBots, savedBots } from "./config/defaults";
import { mergePatch, type BotSettingsPatch } from "./config/mergePatch";
import type { BotsConfig } from "./config/types";
import { botsProblemsOf, type BotsProblem } from "./settings/botsValidation";

/**
 * The cache entry for one project's bot settings: the document exactly as the
 * server answered, plus the etag guarding the next write to it.
 *
 * Under the project's own prefix, like every other thing a project owns, so a
 * rename or a finished wipe takes it with them — see projectQueryKey.
 *
 * Deliberately NOT under `["project", id, "bots"]`, which would have read
 * better. Four screens of the Bots page invalidate that prefix whenever they
 * write a row of their own — the roster, the guild templates, the pool, the
 * phrases — and react-query matches prefixes, so each of those saves would
 * refetch this document UNDER AN OPEN DRAFT: the diff's base and the etag would
 * both be replaced, and the next Save would compute a patch against a version
 * the operator never saw, silently reverting somebody's edit with no conflict
 * dialog, because the If-Match would match.
 */
export function botSettingsQueryKey(projectId: string) {
  return ["project", projectId, "bot-settings"] as const;
}

/**
 * The one draft of `bot_settings.config` the Bots page edits.
 *
 * Settings and Source are two forms over ONE document: they read the same cache
 * entry, patch the same row and race for the same etag. Both of them used to
 * carry their own copy of this machine — the draft overlay, the dirty flag, the
 * beforeunload guard, the `W/`-and-quotes etag strip, the invalidations after a
 * save, the conflict reload — and the copies had already begun to disagree.
 *
 * TWO KEYS ARE NEVER IN A DRAFT. `enabled` is the page header's «Simulation on»
 * switch and `source` is the Source tab's own toggle; both are written straight
 * to the server the moment they are pressed, through `write` below. A draft
 * opened before somebody pressed one must not carry the old value back in on
 * Save, so the server's answer wins for those two on every render.
 *
 * What a Save sends is a MERGE PATCH and not the document — see mergePatch, and
 * the deletion that makes the difference.
 *
 * The project config comes along read-only. It is the second document
 * `bot.Parse` takes on the server, and the forms need it for exactly what the
 * server needs it for: the card schemas a profile rule is checked against, and
 * the chat window the backfill must not outgrow.
 */
export interface BotSettingsDraft {
  /** Neither document has answered yet — no form can draw a field. */
  loading: boolean;
  /** The server's own answer, normalised. */
  serverSettings: BotsConfig;
  /** The server's answer with the local edits laid over it. What every field on
   *  both forms is drawn from, and what Save diffs to build its patch. */
  workingSettings: BotsConfig;
  /** The project's config — READ ONLY here. Edited on the Config page. */
  projectConfig: ProjectConfig;
  dirty: boolean;
  saving: boolean;
  /** What the last save was refused for, already a sentence. */
  saveError: string | null;
  /** …and the fields it named, when it named any. */
  saveProblems: BotsProblem[];
  discardOpen: boolean;
  conflictOpen: boolean;
  edit: (next: (prev: BotsConfig) => BotsConfig) => void;
  /** Send the form's diff. True when it landed — the caller's toast, and its
   *  only reason to care, since every refusal is already on screen. */
  save: () => Promise<boolean>;
  /** Send one key straight away, outside the draft: the two switches. Throws
   *  what the API threw, because those two callers have their own toasts — and
   *  throws rather than resolving quietly when there is nothing to write
   *  against, or a switch pressed before the document loaded would look like a
   *  switch that worked. */
  write: (patch: BotSettingsPatch) => Promise<void>;
  /** The document could not be read at all. Neither form can draw a field, and
   *  neither may pretend it is still loading. */
  loadError: string | null;
  askDiscard: () => void;
  closeDiscard: () => void;
  /** Drop the edits — the Discard dialog's own button, and what a source switch
   *  or a conflict reload does without asking twice. */
  discard: () => void;
  closeConflict: () => void;
  /** Throw the edits away and take the server's newer version. */
  reload: () => Promise<void>;
}

export function useBotSettingsDraft(projectID: string): BotSettingsDraft {
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: botSettingsQueryKey(projectID),
    queryFn: () => getBotSettings(projectID),
    enabled: !!projectID,
  });
  const projectQ = useProject(projectID);

  const [draft, setDraft] = useState<BotsConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveProblems, setSaveProblems] = useState<BotsProblem[]>([]);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);

  // Normalised on the read, never on the way into the cache: the entry holds
  // the document exactly as the server sent it, and every reader shapes its own
  // answer out of that one form.
  const serverSettings = useMemo(
    () =>
      settingsQ.data ? normalizeBots(settingsQ.data.config) : DEFAULT_BOTS_CONFIG(),
    [settingsQ.data],
  );
  const projectConfig = useMemo(
    () => (projectQ.data ? normalizeConfig(projectQ.data.config) : DEFAULT_CONFIG),
    [projectQ.data],
  );

  const workingSettings = useMemo(() => {
    if (!draft) return serverSettings;
    return {
      ...draft,
      enabled: serverSettings.enabled,
      source: serverSettings.source,
    };
  }, [draft, serverSettings]);

  // Compared as Save would send them, so an edit that changes nothing saved
  // never lights the save bar over an empty patch.
  const dirty = !isEqual(savedBots(serverSettings), savedBots(workingSettings));

  // Either document failing leaves both forms unable to draw an honest field:
  // the settings are what they edit, and the project config is the schema their
  // card rules are checked against.
  //
  // The code is taken from the request that actually failed. Reading it off the
  // settings query either way would describe the wrong failure whenever the
  // project is the one that could not be read — an operator told about the bot
  // settings while the thing that broke was the project.
  const failed = settingsQ.error ?? projectQ.error;
  const loadError =
    settingsQ.isError || projectQ.isError
      ? describeError(failed instanceof ApiError ? failed.code : "internal")
      : null;

  // beforeunload guard — attached only while dirty, so a tab with nothing
  // unsaved navigates away without a confirm dialog.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  function edit(next: (prev: BotsConfig) => BotsConfig) {
    setDraft((prev) => next(prev ?? serverSettings));
  }

  function discard() {
    setDraft(null);
    setSaveError(null);
    setSaveProblems([]);
    setDiscardOpen(false);
  }

  /** The saved document back into the one entry every form of it reads. */
  function remember(saved: { config: unknown; etag: string }) {
    qc.setQueryData(botSettingsQueryKey(projectID), saved);
  }

  async function write(patch: BotSettingsPatch) {
    if (!settingsQ.data) {
      // A switch pressed before the document arrived. Thrown rather than
      // ignored: the callers turn a rejection into a toast, and a promise that
      // resolved would have them report a save that never happened.
      throw new Error("the bot settings have not been read yet");
    }
    try {
      remember(await patchBotSettings(projectID, patch, settingsQ.data.etag));
    } catch (err) {
      // These two switches save immediately and have no draft to protect, so a
      // conflict has an obvious answer: take the server's version, so the
      // control shows what is actually true, and let the caller say so. Without
      // the refetch the switch flicks back to a value that is also wrong.
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        await settingsQ.refetch();
      }
      throw err;
    }
    // The Overview tab reads these settings back as problems, layers and
    // thresholds — it is stale the moment this lands.
    await qc.invalidateQueries({
      queryKey: ["project", projectID, "bots", "overview"],
    });
  }

  async function save(): Promise<boolean> {
    if (!settingsQ.data) return false;
    setSaving(true);
    setSaveError(null);
    setSaveProblems([]);
    try {
      const patch = mergePatch(savedBots(serverSettings), savedBots(workingSettings));
      remember(
        await patchBotSettings(projectID, patch, settingsQ.data.etag),
      );
      setDraft(null);
      await qc.invalidateQueries({
        queryKey: ["project", projectID, "activity"],
      });
      await qc.invalidateQueries({
        queryKey: ["project", projectID, "bots", "overview"],
      });
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        setConflictOpen(true);
      } else if (err instanceof ApiError) {
        setSaveError(describeError(err.code));
        setSaveProblems(botsProblemsOf(err));
      } else {
        setSaveError(describeError("internal"));
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function reload() {
    // The dialog promises the current edits are dropped — they are dropped here
    // rather than left for the refetch to overwrite.
    setConflictOpen(false);
    discard();
    await settingsQ.refetch();
  }

  return {
    // `!settingsQ.data` is not a loading state on its own — a read that FAILED
    // has no data either, and reporting that as «loading» is the eternal
    // spinner this branch exists to avoid.
    loading:
      !loadError && (settingsQ.isLoading || !settingsQ.data || projectQ.isLoading),
    loadError,
    serverSettings,
    workingSettings,
    projectConfig,
    dirty,
    saving,
    saveError,
    saveProblems,
    discardOpen,
    conflictOpen,
    edit,
    save,
    write,
    askDiscard: () => setDiscardOpen(true),
    closeDiscard: () => setDiscardOpen(false),
    discard,
    closeConflict: () => setConflictOpen(false),
    reload,
  };
}
